import { memo, useEffect, useState, useCallback } from 'react';
import { Glossary, Common } from '@stockbit/ui';
import debounce from 'lodash.debounce';
import styled from 'styled-components';

import { useRouter } from 'next/router';
import Image from 'global/Image';
import DATA_CY from 'constants/data-cy';

import useStore from '../store';
import { useGlossarySearch } from '../hooks';
import { IGlossaryData } from '../types';
import { DATA_CY_GLOSSARY_SEARCH } from './__tests__/constant';

const { Search: SearchContainer } = Glossary;
const { Input } = Common;

const ImgIcon = styled(Image)`
  width: 16px;
  margin-right: 5px;
`;

const SearchSvg = <ImgIcon src="/icon/cari.svg" alt="search-icon" />;

function Search(): JSX.Element {
  const router = useRouter();
  const [inputSearch, setInputSearch] = useState<string | null>(null);
  const { searchData, setGlossarySearch } = useStore();
  const { data: searchDataQuery } = useGlossarySearch(inputSearch);

  useEffect(() => {
    if (!searchDataQuery) return;

    setGlossarySearch(searchDataQuery);
  }, [searchDataQuery]);

  const onTypeHandler = useCallback(
    debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      const typedVal: string = e.target.value;
      setInputSearch(typedVal);
    }, 500),
    [],
  );

  const onClickSuggestedHandler = (title: string): void => {
    const item = searchData.find(({ name }: IGlossaryData) => name === title);

    if (item) {
      router.push('/glossary/term/[slug]', `/glossary/term/${item.alias}`);
    }
  };

  const cleanedOptions = inputSearch
    ? Array.from(
        new Set(searchData.map(({ name }: IGlossaryData) => name)),
      ).map((value) => ({ value }))
    : [];

  const options = cleanedOptions.map(({ value }) => ({
    value,
    label: (
      <span data-cy={`${DATA_CY.GLOSSARY.SEARCH_SUGGESTION}-${value}`}>
        {value}
      </span>
    ),
  }));

  return (
    <SearchContainer
      style={{ marginBottom: '10px' }}
      options={options}
      onSelect={onClickSuggestedHandler}
    >
      <Input
        data-cy={DATA_CY_GLOSSARY_SEARCH}
        width="100%"
        prefix={SearchSvg}
        placeholder="Search for glossary"
        onChange={onTypeHandler}
      />
    </SearchContainer>
  );
}

export default memo(Search);
